import { defineStore } from "pinia";

export type TToastStatus = "success" | "warning" | "error";

interface IToast {
  text: string;
  status: TToastStatus;
  id: number;
}
type ToastPayload = { timeout?: number; text: string };

const toastTimeoutMap: Record<TToastStatus, number> = {
  success: 5000,
  warning: 7000,
  error: 9000,
};

/**
 * Backend refusals are written for operators and are as long as they need to be — the boarding
 * rate refusal is three sentences because it names the exact catalogue row to create. Nine
 * seconds is not enough to read that, and a message nobody finishes reading has been truncated
 * just as surely as if we had cut it. Dwell time grows with length instead.
 */
const TOAST_READING_MS_PER_CHAR = 45;
const TOAST_MAX_TIMEOUT = 30000;

function dwellTime(text: string, status: TToastStatus): number {
  const base = toastTimeoutMap[status];

  if (status === "success")
    return base;

  return Math.min(TOAST_MAX_TIMEOUT, Math.max(base, text.length * TOAST_READING_MS_PER_CHAR));
}

/**
 * One failure, one toast.
 *
 * A deterministic refusal that reaches the toaster more than once — a retry, an interceptor and
 * a caller both reporting it, a batch where every item hit the same rule — used to stack
 * identical messages, which reads as an intermittent fault rather than one settled answer. The
 * window is short enough that a genuinely repeated action still gets its own confirmation.
 */
const TOAST_DEDUPE_WINDOW_MS = 4000;

const stripHtml = (text: string) => {
  const summaryMatch = text.match(/<summary>([\s\S]*?)<\/summary>/i);
  const source = summaryMatch?.[1] ?? text;
  const withoutTags = source.replace(/<[^>]+>/g, " ");

  if (typeof window === "undefined")
    return withoutTags;

  const textarea = document.createElement("textarea");

  textarea.innerHTML = withoutTags;

  return textarea.value;
};

const normalizeToastText = (text: string) => {
  return stripHtml(String(text ?? ""))
    // Collapse runs of spaces/tabs but KEEP line breaks: a refusal that lists several things puts
    // each on its own line, and flattening them into one paragraph is what made long messages
    // unreadable. `white-space: pre-line` on the toast renders them.
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^[\w.]+Error:\s*/i, "")
    .trim();
};

const toastSoundMap: Record<TToastStatus, string> = {
  success: "/sounds/cat.wav",
  error: "/sounds/dog.wav",
  warning: "/sounds/other-animal.wav",
};

const audioCache = new Map<string, HTMLAudioElement>();

export const playToastSound = (status: TToastStatus) => {
  if (typeof window === "undefined")
    return;

  try {
    const src = toastSoundMap[status];
    const audio = audioCache.get(src) ?? new Audio(src);

    if (!audioCache.has(src)) {
      audio.preload = "auto";
      audioCache.set(src, audio);
    }

    audio.currentTime = 0;
    audio.play().catch((error) => {
      console.error(`Toast sound failed (${src}):`, error);
    });
  }
  catch (error) {
    console.error("playToastSound error:", error);
  }
};

/** `${status}::${text}` → when it was last shown. Pruned on write, so it never grows unbounded. */
const recentToasts = new Map<string, number>();

function seenRecently(key: string): boolean {
  const now = Date.now();

  for (const [seenKey, at] of recentToasts) {
    if (now - at >= TOAST_DEDUPE_WINDOW_MS)
      recentToasts.delete(seenKey);
  }

  const at = recentToasts.get(key);
  const isDuplicate = at !== undefined && now - at < TOAST_DEDUPE_WINDOW_MS;

  recentToasts.set(key, now);

  return isDuplicate;
}

const createToast = (text: string, status: TToastStatus): IToast => ({
  text,
  status,
  id: Date.now() + Math.random(),
});

export default defineStore("toaster-store", {
  state: (): { toasts: IToast[] } => ({
    toasts: [],
  }),
  actions: {
    updateState(payload: ToastPayload, status: TToastStatus) {
      const { timeout } = payload;
      const text = normalizeToastText(payload.text);

      if (!text)
        return;

      // Same words, same severity, still on screen → the operator is already reading it. Adding a
      // second copy tells them nothing and turns one refusal into a wall of identical toasts.
      if (seenRecently(`${status}::${text}`))
        return;

      const toast = createToast(text, status);

      this.toasts.push(toast);
      playToastSound(status);

      setTimeout(() => {
        this.dismiss(toast.id);
      }, timeout ?? dwellTime(text, status));
    },

    /**
     * Long refusals stay up for up to 30s and sit over the page's own buttons, so the operator
     * can close one as soon as they have read it instead of waiting it out.
     */
    dismiss(id: number) {
      const toast = this.toasts.find(t => t.id === id);

      this.toasts = this.toasts.filter((t) => t.id !== id);

      // Closing a message is "I've read it" — the same words may legitimately come back right away.
      if (toast)
        recentToasts.delete(`${toast.status}::${toast.text}`);
    },
    success(payload: ToastPayload) {
      this.updateState(payload, "success");
    },

    warning(payload: ToastPayload) {
      this.updateState(payload, "warning");
    },

    error(payload: ToastPayload) {
      this.updateState(payload, "error");
    },
  },
});
